/**
 * Boot and routing.
 *
 * One screen is mounted at a time, into a fixed stage. Screens are plain
 * functions returning an element, which keeps teardown honest: whatever a
 * screen subscribed to is registered with `onLeave` and unwound before the
 * next one mounts.
 */

import { ctx, disposeScreen } from './ui/ctx';
import { clear, el } from './ui/dom';
import { homeScreen } from './ui/screens/home';
import { playScreen } from './ui/screens/play';
import { revealScreen } from './ui/screens/reveal';
import { archiveScreen } from './ui/screens/archive';
import { closeSheet, resetChrome, toast } from './ui/chrome';
import { Session } from './game/session';
import { score } from './game/grade';
import type { DayResult } from './state/store';
import { isMuted, loadStats, recordResult, resultFor } from './state/store';
import { setMuted as setAudioMuted, unlock } from './ui/audio';
import { todayIndex } from './core/schedule';
import { loadPregenerated, puzzleFor, prewarm } from './ui/cache';
import { applyLive, clearLive } from './ui/paint';
import { haptic } from './ui/motion';

const SCREENS = {
  home: homeScreen,
  play: playScreen,
  reveal: revealScreen,
  archive: archiveScreen,
} as const;

let stage: HTMLElement;

export function boot(): void {
  stage = el('stage');
  applyLive('#8b8b96');

  ctx.stats = loadStats();
  ctx.index = todayIndex();
  ctx.puzzle = puzzleFor(ctx.index);
  ctx.result = resultFor(ctx.index);
  ctx.mode = 'daily';

  setAudioMuted(isMuted());
  installGlobalHandlers();

  // Boot never waits on the network: the file lands when it lands, and local
  // generation covers anything it has not reached yet.
  void loadPregenerated();
  prewarm([ctx.index]);

  // A narrow test seam. Not an API: the e2e suite reads the current puzzle's
  // recipe from it so it can play a guaranteed-winning line instead of hoping
  // two random pigments land inside tolerance.
  (window as unknown as Record<string, unknown>).__prism = { ctx, go, start, finish };

  go('home');
  dismissBoot();
}

/** The only screen change path. Everything else funnels through here. */
export function go(screen: keyof typeof SCREENS): void {
  ctx.screen = screen;
  disposeScreen();
  closeSheet();
  resetChrome();

  if (screen !== 'play') clearLive();

  const node = SCREENS[screen]();
  clear(stage);
  stage.appendChild(node);

  // Focus the stage, not the first control: letting a button grab focus makes
  // mobile keyboards and :focus-visible rings behave oddly on navigation.
  stage.setAttribute('data-screen', screen);
}

export function start(index: number, mode: 'daily' | 'archive'): void {
  // Resuming an unfinished session should not wipe the bench.
  const resuming =
    ctx.session !== null &&
    ctx.index === index &&
    ctx.mode === mode &&
    !ctx.session.isSubmitted;

  ctx.index = index;
  ctx.mode = mode;
  ctx.puzzle = puzzleFor(index);
  ctx.result = resultFor(index);
  ctx.trail = [];
  ctx.startedAt = performance.now();
  if (!resuming) ctx.session = new Session(ctx.puzzle);

  go('play');
}

export function finish(): void {
  const session = ctx.session;
  const puzzle = ctx.puzzle;
  if (!session || !puzzle) return;

  const answer = session.answer;
  if (!answer) return;

  const scored = score(answer.lab, puzzle.target.lab, puzzle.spec.tolerance);
  const result: DayResult = {
    index: puzzle.index,
    grade: scored.grade,
    distance: scored.distance,
    tolerance: puzzle.spec.tolerance,
    blends: session.blendsUsed,
    ms: Math.round(performance.now() - ctx.startedAt),
    hex: answer.hex,
    targetHex: puzzle.target.hex,
    at: Date.now(),
  };

  if (ctx.mode === 'daily') {
    ctx.stats = recordResult(result);
  }

  ctx.result = result;
  go('reveal');
}

export function refresh(): void {
  ctx.stats = loadStats();
  ctx.result = resultFor(ctx.index);
  go(ctx.screen);
}

// ---------------------------------------------------------------------------
// Global behaviour
// ---------------------------------------------------------------------------

function installGlobalHandlers(): void {
  Object.assign(ctx, { go, start, finish, refresh });

  // Audio may only start inside a gesture. The first tap anywhere unlocks it.
  const unlockOnce = () => {
    unlock();
    haptic('tap');
  };
  window.addEventListener('pointerdown', unlockOnce, { once: true, passive: true });

  // A day rolling over while the tab sits open should refresh home rather than
  // leave a stale puzzle on screen.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    const now = todayIndex();
    if (now !== ctx.index && ctx.screen !== 'play') {
      ctx.index = now;
      ctx.puzzle = puzzleFor(now);
      ctx.result = resultFor(now);
      ctx.mode = 'daily';
      ctx.session = null;
      go('home');
    }
  });

  // Swipe back from the play screen; Escape closes whatever is on top.
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !document.querySelector('.sheet')) {
      if (ctx.screen === 'play') go('home');
      else if (ctx.screen === 'reveal') go('home');
    }
  });

  // Back gesture on Android, via the history stack.
  window.addEventListener('popstate', () => {
    if (ctx.screen !== 'home') {
      history.pushState({ screen: ctx.screen }, '');
      go('home');
    }
  });

  // Nudge the streak back if the player returns after the day has turned.
  window.addEventListener('focus', () => {
    if (ctx.screen === 'home') {
      const stats = loadStats();
      if (stats.streak !== ctx.stats.streak) ctx.stats = stats;
    }
  });

  window.addEventListener('online', () => toast('Back online'));
  window.addEventListener('offline', () => toast('Offline — that is fine, ColorDrift works without a network'));

  void history.replaceState({ screen: 'home' }, '');
}

function dismissBoot(): void {
  const boot = document.getElementById('boot');
  if (!boot) return;
  requestAnimationFrame(() => {
    boot.style.opacity = '0';
    setTimeout(() => boot.remove(), 500);
  });
}
